import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, DollarSign, FileText, Check, History, User, Plus, Download } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { API_BASE } from '../../utils/constants';
import LoadingSpinner from '../ui/LoadingSpinner';
import ReceiptGenerator from '../ui/ReceiptGenerator';

const GroupDetails = () => {
  const { id } = useParams();
  const [groupData, setGroupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [settlementOptions, setSettlementOptions] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  const loadGroupDetails = async () => {
    try {
      console.log('Loading group details for ID:', id);
      console.log('Current user:', user.email);

      const userRes = await fetch(`${API_BASE}/users/email/${user.email}`);
      if (!userRes.ok) {
        throw new Error('Failed to fetch user data');
      }
      const userData = await userRes.json();
      console.log('User data:', userData);

      const res = await fetch(`${API_BASE}/groups/${id}/details?userId=${userData.id}`);
      if (!res.ok) {
        throw new Error(`Failed to fetch group data: ${res.status} ${res.statusText}`);
      }

      const group = await res.json();
      console.log('Group data loaded:', group);
      setGroupData(group);
    } catch (err) {
      console.error('Error loading group details:', err);
      if (window.showNotification) {
        window.showNotification('error', `Failed to load group data: ${err.message}`);
      }
      navigate('/dashboard');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user && user.email && id) {
      loadGroupDetails();
    }
  }, [user, id]);

  const handleSettle = async (payeeId) => {
    try {
      console.log('Settling up with payee:', payeeId);
      
      const userRes = await fetch(`${API_BASE}/users/email/${user.email}`);
      const userData = await userRes.json();

      const res = await fetch(`${API_BASE}/settle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          payerId: userData.id, 
          payeeId, 
          groupId: parseInt(id) 
        })
      });

      if (res.ok) {
        const response = await res.text();
        if (window.showNotification) {
          window.showNotification('success', response);
        }
        setShowSettleModal(false);
        loadGroupDetails();
      } else {
        const errorText = await res.text();
        if (window.showNotification) {
          window.showNotification('error', `Failed to settle up: ${errorText}`);
        }
      }
    } catch (err) {
      console.error('Settlement error:', err);
      if (window.showNotification) {
        window.showNotification('error', 'Something went wrong during settlement.');
      }
    }
  };

  const openSettleModal = () => {
    if (!groupData || !groupData.balances) return;
    
    const options = [];
    console.log('Processing balances for settlement:', groupData.balances);
    
    groupData.balances.forEach(msg => {
      const match = msg.match(/^You owe (.+) ₹([\d.]+)/);
      if (match) {
        const name = match[1];
        const amount = match[2];
        const member = groupData.members.find(m => m.name === name);
        if (member) {
          options.push({ ...member, amount });
        }
      }
    });
    
    console.log('Settlement options:', options);
    setSettlementOptions(options);
    setShowSettleModal(true);
  };

  const handleGenerateReceipt = (expense) => {
    setSelectedExpense(expense);
    setShowReceiptModal(true);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!groupData) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Group not found</h1>
          <p className="mt-2 text-gray-600">The group you're looking for doesn't exist or you don't have access to it.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center text-teal-600 hover:text-teal-700 mb-4 transition-colors"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Group: {groupData.name}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Members Section */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <Users className="mr-2 h-5 w-5 text-teal-600" />
            Members ({groupData.members?.length || 0})
          </h2>
          {groupData.members && groupData.members.length > 0 ? (
            <ul className="space-y-3">
              {groupData.members.map((member) => (
                <li key={member.id} className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-teal-600" />
                  </div>
                  <div>
                    <span className="text-gray-900 font-medium">{member.name}</span>
                    <p className="text-sm text-gray-500">{member.email}</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500">No members found.</p>
          )}
        </div>

        {/* Balances Section */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <DollarSign className="mr-2 h-5 w-5 text-teal-600" />
            Your Balances
          </h2>
          {groupData.isSettled ? (
            <div className="flex items-center space-x-2 text-green-600 bg-green-50 p-4 rounded-md">
              <Check className="h-5 w-5" />
              <span className="font-medium">You are all settled up! 🎉</span>
            </div>
          ) : (
            <div className="space-y-2">
              {groupData.balances && groupData.balances.length > 0 ? (
                groupData.balances.map((balance, index) => (
                  <div key={index} className={`p-3 rounded-md text-sm ${
                    balance.includes('owe') ? 'bg-red-50 text-red-700 border-l-4 border-red-400' : 
                    balance.includes('owes you') ? 'bg-green-50 text-green-700 border-l-4 border-green-400' : 
                    'bg-gray-50 text-gray-700'
                  }`}>
                    <div className="flex items-center">
                      {balance.includes('owe') ? (
                        <span className="mr-2">💸</span>
                      ) : balance.includes('owes you') ? (
                        <span className="mr-2">💰</span>
                      ) : (
                        <span className="mr-2">⚖️</span>
                      )}
                      {balance}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-6">
                  <DollarSign className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500">No balances to display.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expenses Section */}
      <div className="mt-8 bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <FileText className="mr-2 h-5 w-5 text-teal-600" />
            Recent Expenses ({groupData.expenses?.length || 0})
          </h2>
          <button
            onClick={() => navigate('/add-expense')}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-teal-700 bg-teal-100 hover:bg-teal-200 transition-colors"
          >
            <Plus className="mr-1 h-3 w-3" />
            Add Expense
          </button>
        </div>
        
        {groupData.expenses && groupData.expenses.length > 0 ? (
          <div className="space-y-3">
            {groupData.expenses.map((expense) => (
              <div key={expense.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <p className="font-medium text-gray-900">{expense.description}</p>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <FileText className="h-3 w-3 mr-1" />
                      Expense
                    </span>
                  </div>
                  <div className="mt-1 flex items-center space-x-4 text-sm text-gray-600">
                    <span>Paid by <span className="font-medium">{expense.payerName}</span></span>
                    <span>•</span>
                    <span>{expense.date ? new Date(expense.date).toLocaleDateString('en-IN') : 'Unknown date'}</span>
                  </div>
                </div>
                <div className="text-right flex items-center space-x-3">
                  <div>
                    <span className="text-lg font-semibold text-gray-900">₹{expense.amount}</span>
                    <p className="text-xs text-gray-500">
                      ₹{(expense.amount / (groupData.members?.length || 1)).toFixed(2)} per person
                    </p>
                  </div>
                  {/* Receipt Button */}
                  <button
                    onClick={() => handleGenerateReceipt(expense)}
                    className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-full transition-colors"
                    title="Generate Receipt"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No expenses yet</h3>
            <p className="mt-1 text-sm text-gray-500">Add your first expense to get started splitting costs!</p>
            <button
              onClick={() => navigate('/add-expense')}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 transition-colors"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add First Expense
            </button>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="mt-8 flex flex-col sm:flex-row gap-4">
        <button
          onClick={openSettleModal}
          disabled={groupData.isSettled}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Check className="mr-2 h-5 w-5" />
          Settle Up
        </button>
        <button
          onClick={() => setShowHistoryModal(true)}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-teal-700 bg-teal-100 hover:bg-teal-200 transition-colors"
        >
          <History className="mr-2 h-5 w-5" />
          View Transaction History
        </button>
        <button
          onClick={() => navigate('/add-expense')}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-teal-700 bg-teal-100 hover:bg-teal-200 transition-colors"
        >
          <Plus className="mr-2 h-5 w-5" />
          Add Expense
        </button>
      </div>

      {/* Settle Modal */}
      {showSettleModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900">Settle Up</h3>
              <div className="mt-4">
                {settlementOptions.length === 0 ? (
                  <p className="text-gray-600">No outstanding debts to settle 🎉</p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 mb-3">Choose who to settle with:</p>
                    {settlementOptions.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => handleSettle(option.id)}
                        className="w-full p-3 text-left border rounded-md hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-medium">{option.name}</span>
                          <span className="text-teal-600 font-semibold">₹{option.amount}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => setShowSettleModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Transaction History</h3>
              <div className="max-h-96 overflow-y-auto space-y-3">
                {groupData.expenses && groupData.expenses.length > 0 && 
                  groupData.expenses.map((expense) => (
                    <div key={`expense-${expense.id}`} className="p-3 bg-green-50 rounded-md border-l-4 border-green-400">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <DollarSign className="h-4 w-4 text-green-600 mr-2" />
                          <span className="text-sm">
                            <strong>{expense.payerName}</strong> paid ₹{expense.amount} for <em>{expense.description}</em>
                            <span className="text-gray-500 ml-2">
                              ({expense.date ? new Date(expense.date).toLocaleDateString('en-IN') : 'Unknown date'})
                            </span>
                          </span>
                        </div>
                        <button
                          onClick={() => {
                            handleGenerateReceipt(expense);
                            setShowHistoryModal(false);
                          }}
                          className="p-1 text-gray-400 hover:text-teal-600 rounded"
                          title="Generate Receipt"
                        >
                          <Download className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ))
                }
                {groupData.settlements && groupData.settlements.length > 0 && 
                  groupData.settlements.map((settlement, index) => (
                    <div key={`settlement-${index}`} className="p-3 bg-blue-50 rounded-md border-l-4 border-blue-400">
                      <div className="flex items-center">
                        <Check className="h-4 w-4 text-blue-600 mr-2" />
                        <span className="text-sm">
                          <strong>{settlement.payerName}</strong> settled ₹{settlement.amount} with <strong>{settlement.payeeName}</strong>
                          <span className="text-gray-500 ml-2">
                            ({settlement.date ? new Date(settlement.date).toLocaleDateString('en-IN') : 'Unknown date'})
                          </span>
                        </span>
                      </div>
                    </div>
                  ))
                }
                {(!groupData.expenses || groupData.expenses.length === 0) && 
                 (!groupData.settlements || groupData.settlements.length === 0) && (
                  <div className="text-center text-gray-500 py-8">
                    <History className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                    <p>No transaction history yet.</p>
                  </div>
                )}
              </div>
              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Generator Modal */}
      <ReceiptGenerator
        expense={selectedExpense}
        group={groupData}
        isOpen={showReceiptModal}
        onClose={() => {
          setShowReceiptModal(false);
          setSelectedExpense(null);
        }}
      />
    </div>
  );
};

export default GroupDetails;